import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type {
  ForceableBlockVerdict,
  QaDeleteCustomerPreview,
  QaDeleteCustomerResult,
  QaDwdOverrideStatus,
  QaForceableEndpoint,
  QaInviteRejectMode,
  QaOverridesState,
  QaSyncOutcome,
} from '@cdw-ae/common-data';
import type { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * INTERNAL QA actions for the console's "QA Overrides" page. Everything here is
 * confined server-side to the QA test-domain allowlist. The platform-auth
 * interceptor attaches the Bearer token + x-cdw-app-name, so no headers here.
 */
@Injectable({ providedIn: 'root' })
export class PlatformQaService {
  private readonly http = inject(HttpClient);

  /**
   * Clear ALL T&C acceptances for a test customer so QA can replay Wizard
   * Step 1 as the same user. Returns how many rows were removed.
   */
  clearTerms(domain: string): Observable<{ cleared: number }> {
    const url = `${environment.urls.backendApi}/platform/qa/clear-terms`;
    return this.http.post<{ cleared: number }>(url, { domain });
  }

  /**
   * Force the Step-3 DWD verify status for a test customer so QA can reach the
   * verification-failure views (delegate_not_found / id_mismatch /
   * delegate_lacks_admin / delegate_inactive / error). Ephemeral, self-healing,
   * test-domains only.
   */
  setDwdOverride(
    domain: string,
    status: QaDwdOverrideStatus
  ): Observable<{ applied: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/dwd-override`;
    return this.http.post<{ applied: boolean }>(url, { domain, status });
  }

  clearDwdOverride(domain: string): Observable<{ cleared: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/dwd-override/clear`;
    return this.http.post<{ cleared: boolean }>(url, { domain });
  }

  /**
   * Age the Step-3 propagation re-check clock (EDUS-2844) so the 24-hour states
   * are reachable without waiting a day. `applied: false` means nothing is
   * re-checking for that tenant yet.
   */
  backdateDwdRecheck(
    domain: string,
    hoursAgo: number
  ): Observable<{ applied: boolean; startedAt: string | null }> {
    const url = `${environment.urls.backendApi}/platform/qa/dwd-recheck-backdate`;
    return this.http.post<{ applied: boolean; startedAt: string | null }>(url, {
      domain,
      hoursAgo,
    });
  }

  /**
   * Run one link of the re-check chain now, through the same handler Cloud Tasks
   * calls — so the background success path is observable without waiting out the
   * hourly cadence.
   */
  runDwdRecheck(domain: string): Observable<{ ran: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/dwd-recheck-run`;
    return this.http.post<{ ran: boolean }>(url, { domain });
  }

  /**
   * Toggle "force the DWD client-id fetch to fail" for a test customer, so QA can
   * reach Step 3's "couldn't load your authorization details" load-error screen
   * (a distinct, earlier failure than the DWD verify outcomes). ~1h TTL.
   */
  setClientIdFailOverride(
    domain: string,
    enabled: boolean
  ): Observable<{ applied: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/client-id-fail-override`;
    return this.http.post<{ applied: boolean }>(url, { domain, enabled });
  }

  /**
   * Toggle "force this onboarding request to fail" for ONE endpoint on a test
   * customer, so QA can reach the wizard's error and fallback screens — the one
   * category of state that renders from a caught HTTP failure and therefore had
   * no lever at all. Each endpoint is its own marker, so several can be armed at
   * once. ~1h TTL.
   */
  setEndpointFailOverride(
    domain: string,
    endpoint: QaForceableEndpoint,
    enabled: boolean
  ): Observable<{ applied: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/endpoint-fail-override`;
    return this.http.post<{ applied: boolean }>(url, {
      domain,
      endpoint,
      enabled,
    });
  }

  /**
   * Delay EVERY onboarding request for a test customer, so the wizard's
   * transient spinners are visible rather than gone in a flash. Pass 0 to clear.
   * Capped server-side; ~1h TTL.
   */
  setSpinnerDelay(
    domain: string,
    delayMs: number
  ): Observable<{ applied: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/spinner-delay`;
    return this.http.post<{ applied: boolean }>(url, { domain, delayMs });
  }

  /**
   * Make the Step-5 send report Mandrill rejections on an HTTP 200 — the path a
   * real rejection takes, and the one the forced-failure lever cannot reach
   * (that fails the request instead). Pass null to disarm. ~1h TTL.
   */
  setInviteRejectOverride(
    domain: string,
    mode: QaInviteRejectMode | null
  ): Observable<{ applied: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/invite-reject-override`;
    return this.http.post<{ applied: boolean }>(url, { domain, mode });
  }

  /**
   * Toggle "force the Step-4 sync kickoff to fail" for a test customer, so QA
   * can reach the "Sync Could Not Start" view. ~1h TTL, self-healing.
   */
  setSyncFailOverride(
    domain: string,
    enabled: boolean
  ): Observable<{ applied: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/sync-fail-override`;
    return this.http.post<{ applied: boolean }>(url, { domain, enabled });
  }

  /**
   * Arm a terminal outcome for the tenant's NEXT sync. The kickoff then runs
   * normally — seeding the run and its notification list from the real button
   * press — but skips the real enqueue and reports the armed outcome instead.
   */
  setSyncOutcomeOverride(
    domain: string,
    /** Null disarms the lever. */
    outcome: QaSyncOutcome | null
  ): Observable<{ applied: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/sync-outcome-override`;
    return this.http.post<{ applied: boolean }>(url, { domain, outcome });
  }

  /** Everything currently forced for a domain — the readback the page had none of. */
  getOverrides(domain: string): Observable<QaOverridesState> {
    const url = `${environment.urls.backendApi}/platform/qa/overrides`;
    return this.http.get<QaOverridesState>(url, { params: { domain } });
  }

  /** What "Delete test customer" would remove — the customer and per-table row counts. */
  previewDeleteCustomer(domain: string): Observable<QaDeleteCustomerPreview> {
    const url = `${environment.urls.backendApi}/platform/qa/delete-customer/preview`;
    return this.http.get<QaDeleteCustomerPreview>(url, { params: { domain } });
  }

  /** Delete a test customer outright (EDUS-3040). Cannot be undone. */
  deleteCustomer(domain: string): Observable<QaDeleteCustomerResult> {
    const url = `${environment.urls.backendApi}/platform/qa/delete-customer`;
    return this.http.post<QaDeleteCustomerResult>(url, { domain });
  }

  /** Drop every override for a domain at once. Does NOT touch T&C acceptances. */
  clearAllOverrides(domain: string): Observable<{ cleared: true }> {
    const url = `${environment.urls.backendApi}/platform/qa/overrides/clear-all`;
    return this.http.post<{ cleared: true }>(url, { domain });
  }

  /**
   * Force a login BLOCK verdict for a test domain, so every block screen is one
   * dropdown away rather than a contrivance per screen. The verdict list holds
   * no access-granting verdict, so this can only ever block. ~1h TTL.
   */
  setVerdictOverride(
    domain: string,
    verdict: ForceableBlockVerdict
  ): Observable<{ applied: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/verdict-override`;
    return this.http.post<{ applied: boolean }>(url, { domain, verdict });
  }

  clearVerdictOverride(domain: string): Observable<{ cleared: boolean }> {
    const url = `${environment.urls.backendApi}/platform/qa/verdict-override/clear`;
    return this.http.post<{ cleared: boolean }>(url, { domain });
  }
}
